import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Loads image only when near the viewport (or optional scroll root).
 * Use scrollRoot for horizontal carousels so off-screen cards do not fetch.
 */
export default function LazyImage({
  src,
  alt = '',
  className = 'relative w-full h-full',
  imgClassName = '',
  scrollRoot = null,
  scrollRootRef = null,
  rootMargin = '64px',
  fallbackSrc = null,
  allowFallback = true,
  onError,
  placeholder = null
}) {
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setIsInView(false);
    setIsLoaded(false);
    setHasError(false);
    setUseFallback(false);
  }, [src, fallbackSrc]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !src) return;

    const rootEl = scrollRoot || scrollRootRef?.current || null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: rootEl,
        rootMargin,
        threshold: 0.01
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [src, scrollRoot, scrollRootRef, rootMargin]);

  const activeSrc = useFallback && allowFallback && fallbackSrc ? fallbackSrc : src;

  const handleError = (e) => {
    if (allowFallback && !useFallback && fallbackSrc && fallbackSrc !== src) {
      setUseFallback(true);
      setIsLoaded(false);
      return;
    }
    setHasError(true);
    setIsLoaded(true);
    onError?.(e);
  };

  return (
    <div ref={containerRef} className={className}>
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 z-0 flex items-center justify-center bg-slate-200 animate-pulse">
          {placeholder || <Loader2 size={20} className="animate-spin text-slate-400" />}
        </div>
      )}

      {isInView && activeSrc && !hasError ? (
        <img
          src={activeSrc}
          alt={alt}
          className={`${imgClassName} relative z-10 transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={handleError}
        />
      ) : null}

      {hasError && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-100 text-[10px] text-slate-400">
          No image
        </div>
      )}
    </div>
  );
}
